import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, UsersIcon, CpuIcon, TerminalIcon, CheckCircle2Icon } from 'lucide-react';
type EndpointId = 'search' | 'people' | 'tech';
interface Field {
  key: string;
  label: string;
  placeholder: string;
}
interface Endpoint {
  id: EndpointId;
  label: string;
  icon: React.ElementType;
  description: string;
  method: string;
  path: string;
  fields: Field[];
  buttonLabel: string;
  loadingLabel: string;
  mockResponse: string;
}
const endpoints: Endpoint[] = [{
  id: 'search',
  label: 'Search local businesses',
  icon: SearchIcon,
  description: 'Search local business records using query, tags, location, and quality filters.',
  method: 'POST',
  path: '/api/v1/search',
  fields: [{
    key: 'query',
    label: 'What are you looking for?',
    placeholder: 'coffee shops'
  }, {
    key: 'location',
    label: 'Where?',
    placeholder: 'Los Angeles, CA'
  }],
  buttonLabel: 'Search now',
  loadingLabel: 'Searching...',
  mockResponse: `// Response (50+ fields per local business)
{
  "company_name": "Blue Bottle Coffee",
  "company_type": "cafe",
  "street_address": "123 Main St",
  "city": "Los Angeles",
  "state": "CA",
  "store_emails": ["hello@bluebottle.com"],
  "company_phones": ["+1 (310) 555-0199"],
  "website_url": "https://bluebottlecoffee.com",
  "google_rating": 4.8,
  "google_reviews_count": 1245,
  "social_media_links": {
    "INSTAGRAM": ["@bluebottle"]
  },
  "tags": ["cafe", "specialty coffee"]
}`
}, {
  id: 'people',
  label: 'Find decision makers',
  icon: UsersIcon,
  description: 'Find verified contact information for key decision makers at any company.',
  method: 'POST',
  path: '/api/v1/task/batch/find_people',
  fields: [{
    key: 'domain',
    label: 'Company website',
    placeholder: 'bluebottlecoffee.com'
  }, {
    key: 'title',
    label: 'Job title',
    placeholder: 'CEO'
  }],
  buttonLabel: 'Find people',
  loadingLabel: 'Searching...',
  mockResponse: `// Response
{
  "results": [
    {
      "name": "James Freeman",
      "title": "CEO",
      "email": "james@bluebottlecoffee.com",
      "linkedin_url": "linkedin.com/in/jfreeman",
      "confidence_score": 0.98
    }
  ]
}`
}, {
  id: 'tech',
  label: 'Detect tech stack',
  icon: CpuIcon,
  description: 'Detect a specific technology used by a company website. 1-100 items per request.',
  method: 'POST',
  path: '/api/v1/task/batch/find_tech',
  fields: [{
    key: 'domain',
    label: 'Website domain',
    placeholder: 'joes-italian-bistro.com'
  }, {
    key: 'tech_type',
    label: 'Technology to detect',
    placeholder: 'OpenTable'
  }],
  buttonLabel: 'Detect stack',
  loadingLabel: 'Analyzing...',
  mockResponse: `// Response
{
  "batch_id": "batch_t4k7m2",
  "submit_for": "find_tech",
  "status": {
    "total": 1,
    "completed": 1,
    "processing": 0,
    "errored": 0,
    "batch_ready": true
  },
  "results": [
    {
      "domain": "joes-italian-bistro.com",
      "tech_type": "OpenTable",
      "found": true,
      "link": "https://opentable.com/joes-italian-bistro",
      "confidence": 0.97
    }
  ]
}`
}];
export function ApiPlayground() {
  const [activeEndpoint, setActiveEndpoint] = useState<EndpointId>('search');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [isTyping, setIsTyping] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [displayedResponse, setDisplayedResponse] = useState('');
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const current = endpoints.find(ep => ep.id === activeEndpoint)!;
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);
  const startTyping = (endpoint: Endpoint) => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    setIsTyping(true);
    setHasRun(true);
    setDisplayedResponse('');
    const fullText = endpoint.mockResponse;
    let currentIndex = 0;
    typingIntervalRef.current = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedResponse(fullText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
        }
      }
    }, 15);
  };
  const handleTabSwitch = (id: EndpointId) => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    setActiveEndpoint(id);
    setHasRun(false);
    setIsTyping(false);
    setDisplayedResponse('');
    setFieldValues({});
    const ep = endpoints.find(e => e.id === id)!;
    setTimeout(() => startTyping(ep), 400);
  };
  // Auto-start on initial mount
  useEffect(() => {
    const timer = setTimeout(() => startTyping(current), 600);
    return () => clearTimeout(timer);
  }, []);
  const renderHighlightedJson = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.trim().startsWith('//')) {
        return <div key={i} className="text-slate-500" data-id="element-583">
            {line}
          </div>;
      }
      const parts = line.split(/(".*?"\s*:|".*?"|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?)/g);
      return <div key={i} data-id="element-584">
          {parts.map((part, j) => {
          if (!part) return null;
          if (part.match(/^".*?"\s*:$/)) return <span key={j} className="text-slate-400" data-id="element-585">
                  {part}
                </span>;
          if (part.match(/^".*"$/)) return <span key={j} className="text-green-400" data-id="element-586">
                  {part}
                </span>;
          if (part.match(/^(true|false)$/)) return <span key={j} className="text-yellow-400" data-id="element-587">
                  {part}
                </span>;
          if (part.match(/^-?\d+(?:\.\d+)?$/)) return <span key={j} className="text-blue-400" data-id="element-588">
                  {part}
                </span>;
          return <span key={j} data-id="element-589">{part}</span>;
        })}
        </div>;
    });
  };
  return <section className="py-24 bg-white" data-id="element-590">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-591">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-592">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-593">
            Try the <span className="text-blue-600" data-id="element-594">local business API</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-595">
            Search local businesses, find decision makers, or detect tech stacks
            — see real API responses with verified data.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-596">
          {endpoints.map(ep => {
          const Icon = ep.icon;
          const isActive = activeEndpoint === ep.id;
          return <button key={ep.id} onClick={() => handleTabSwitch(ep.id)} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${isActive ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-597">
                <Icon className="w-4 h-4" data-id="element-598" />
                {ep.label}
              </button>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start" data-id="element-599">
          {/* Left Column: Form */}
          <div className="lg:col-span-5 bg-slate-50 rounded-[2.5rem] p-10" data-id="element-600">
            <div className="flex items-center gap-3 mb-2" data-id="element-601">
              <TerminalIcon className="w-5 h-5 text-slate-400" data-id="element-602" />
              <h3 className="text-lg font-bold text-slate-900" data-id="element-603">
                {current.label}
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-8" data-id="element-604">{current.description}</p>

            <div className="space-y-5" data-id="element-605">
              {current.fields.map(field => <div key={field.key} data-id="element-606">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-607">
                    {field.label}
                  </label>
                  <input type="text" value={fieldValues[field.key] || ''} onChange={e => setFieldValues({
                ...fieldValues,
                [field.key]: e.target.value
              })} placeholder={field.placeholder} className="w-full px-5 py-3.5 bg-white border-none rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-base shadow-sm" data-id="element-608" />
                </div>)}
            </div>
          </div>

          {/* Right Column: Response Viewer */}
          <div className="lg:col-span-7 bg-slate-900 rounded-[2.5rem] overflow-hidden flex flex-col h-[500px]" data-id="element-609">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-800/50" data-id="element-610">
              <div className="flex items-center gap-3" data-id="element-611">
                <div className="flex gap-2" data-id="element-612">
                  <div className="w-3.5 h-3.5 rounded-full bg-slate-700" data-id="element-613" />
                  <div className="w-3.5 h-3.5 rounded-full bg-slate-700" data-id="element-614" />
                  <div className="w-3.5 h-3.5 rounded-full bg-slate-700" data-id="element-615" />
                </div>
                <span className="ml-4 text-sm font-medium text-slate-400 font-mono" data-id="element-616">
                  {current.method} {current.path}
                </span>
              </div>
              {hasRun && !isTyping && <div className="flex items-center gap-2 text-sm font-bold text-green-400 bg-green-400/10 px-3 py-1.5 rounded-full" data-id="element-617">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-618" />
                  200 OK
                </div>}
            </div>

            <div className="flex-1 p-8 overflow-auto font-mono text-sm text-slate-300 leading-relaxed" data-id="element-619">
              {!hasRun && !isTyping ? <div className="h-full flex items-center justify-center text-slate-500 text-lg" data-id="element-620">
                  Click &quot;{current.buttonLabel}&quot; to see the response
                </div> : <pre className="m-0 whitespace-pre-wrap break-words" data-id="element-621">
                  {renderHighlightedJson(displayedResponse)}
                  {isTyping && <span className="inline-block w-2.5 h-5 bg-blue-400 ml-1 animate-pulse align-middle" data-id="element-622" />}
                </pre>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}